/**
 * Firmware registry + campaign fixtures.
 *
 * These are the records behind `GET /ops/v1/firmware`, `GET /ops/v1/campaigns` and
 * `GET /ops/v1/campaigns/{id}` in fixture mode. Two rules shape them:
 *
 *  1. **Nothing is hand-counted.** A campaign record stores what the façade stores —
 *     `targets`, `per_wave`, `queued`, `jobs` — and every number the console reads
 *     (`population`, `progress`, `remaining_devices`, `next_action`) is derived from
 *     those by `fixtures/campaignEngine.ts`, the port of the server's own functions.
 *     The populations come out of `FIXTURE_DEVICES` through `computeTargets`, so a
 *     campaign here describes the fleet the devices page is already showing.
 *  2. **The set covers every verdict.** §۴.۱۳'s question has six answers
 *     (`advance`, `continue_wave`, `wait_jobs`, `wait_window`, `idle`, `settled`) and a
 *     page can only be built against all six if the data holds all six.
 *
 * `file_id` note: the server derives it as `fw:<sha256(brand|model|version)[:12]>`
 * (`openapi.yaml`, `/ops/v1/firmware` post). The fixture cannot hash, so its ids keep
 * the same `fw:<12 hex>` form without being that digest — the same difference the
 * header's `DataSourceBadge` exists to make visible.
 */

import type { CampaignFilter, CampaignKind, FirmwareFile } from '../api/types';
import {
  computeTargets,
  haltTriggered,
  terminalFor,
  waveCounts,
  waveJobCounts,
  type CampaignDoc,
  type CampaignPopulationDevice,
  type CampaignJobEntry,
  type CampaignJobReader,
  type CampaignJobView,
} from './campaignEngine';
import { FIXTURE_DEVICES, isoAgo } from './devices';

/** A lowercase 64-hex digest in the shape the contract pins (`^[0-9a-f]{64}$`). */
function digest(seed: string): string {
  const hex = '0123456789abcdef';
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h = Math.imul(h ^ seed.charCodeAt(i), 16777619) >>> 0;
  }
  let out = '';
  for (let i = 0; i < 64; i += 1) {
    h = (Math.imul(h, 1103515245) + 12345) >>> 0;
    out += hex[(h >>> (i % 12)) % 16];
  }
  return out;
}

/** One registered image: metadata only, exactly like the registry row. */
function image(
  fileId: string,
  brand: string,
  model: string,
  version: string,
  sizeBatches: number,
  agoMinutes: number,
): FirmwareFile {
  const key = `${brand}|${model}|${version}`;
  return {
    file_id: fileId,
    brand,
    model,
    version,
    checksum_sha256: digest(key),
    // a real ONT image is 20-100 MB — the size that made G-FW-01 a gap at all
    size_bytes: sizeBatches * 1_048_576,
    status: 'registered',
    genieacs_file: `firmware/${fileId.slice(3)}.bin`,
    created_at: isoAgo(agoMinutes),
  };
}

/**
 * The registry, one image per brand the fleet actually holds
 * (`docs/VENDOR_MATRIX_FA.md`; the owner's rule is maximum brand coverage).
 */
export const FIXTURE_FIRMWARE: readonly FirmwareFile[] = [
  image('fw:4a7c19d2b0e3', 'Huawei', 'HS8546X6', 'V3R020C10.999', 28, 640),
  image('fw:91be07c4d5a2', 'Huawei', 'HS8546X6', 'V3R020C10.777', 27, 5120),
  image('fw:2fd8a1c6b470', 'ZTE', 'F6600V9', 'V5.0.8P1T2', 31, 300),
  image('fw:c30b59e7a18d', 'C-Data', 'GPT-741X', '3.5.3-2405', 22, 1440),
  image('fw:7e120c8f6b45', 'Fiberhome', 'AN5551-04-B', 'R5.0.3P1', 25, 4320),
  image('fw:b6540d3a9c21', 'VSOL', 'V1500GP-93128', '3.3.3-240601', 19, 180),
];

export function firmwareImageOf(fileId: string): FirmwareFile | undefined {
  return FIXTURE_FIRMWARE.find((row) => row.file_id === fileId);
}

/**
 * `_previous_image` (firmware.py:622-644) — the most recent OTHER campaign image for the
 * same brand/model that is still registered.
 *
 * What makes an image the previous one is campaign history, not registry order: several
 * images of one model can sit in the registry unused, and a deleted row disqualifies its
 * candidate because the FS object may be gone with it.
 */
export function previousImageOf(
  campaignsNewestFirst: readonly FixtureCampaign[],
  registry: readonly FirmwareFile[],
  doc: CampaignDoc,
): FirmwareFile | undefined {
  for (const other of campaignsNewestFirst) {
    const otherDoc = other.doc;
    if (otherDoc.campaign_id === doc.campaign_id) continue;
    if (otherDoc.brand !== doc.brand || otherDoc.model !== doc.model) continue;
    const fileId = otherDoc.file_id ?? '';
    if (!fileId || fileId === doc.file_id) continue;
    const image = registry.find((row) => row.file_id === fileId);
    if (image) return image;
  }
  return undefined;
}

/* ------------------------------------------------------------------ the seed spec --- */

/**
 * How one wave's dispatched devices ended. `open` are the jobs the verify pass has not
 * concluded (still `running`), `failed` the boards that refused; everything else is a
 * `completed` job carrying the `applied` verdict — the only verdict that closes a wave
 * (`campaigns.py:130-152`).
 */
export interface WaveFate {
  failed?: number;
  open?: number;
}

interface CampaignSpec {
  campaign_id: string;
  /** short tag used inside the deterministic job ids */
  tag: string;
  file_id: string;
  filter: CampaignFilter;
  pcts: number[];
  current_wave: number;
  waves: WaveFate[];
  /** devices of the CURRENT wave the rate limit has not dispatched */
  queued?: number;
  status?: string;
  halt_on_fail_pct?: number;
  maintenance_window?: string | null;
  dispatch_kind?: CampaignKind;
  dispatch_file_id?: string;
  started_ago: number;
}

/** The store's job records the campaign reads derive `progress` from. */
export type FixtureCampaignJobs = Record<string, CampaignJobView>;

export interface FixtureCampaign {
  doc: CampaignDoc;
  jobs: FixtureCampaignJobs;
}

/**
 * Build one campaign record the way a sequence of real dispatch passes would have.
 *
 * Waves before `current_wave` are complete (their whole slice was dispatched); the
 * current wave is dispatched except `queued`, which is what `:resume` continues and
 * what the campaign worker would continue on its next pass.
 */
export function buildCampaign(spec: CampaignSpec): FixtureCampaign {
  const row = firmwareImageOf(spec.file_id);
  if (!row) throw new Error(`fixture campaign points at an unregistered image: ${spec.file_id}`);
  const targets = computeTargets(
    campaignPopulation(),
    { version: row.version },
    spec.filter,
    snapshotVersion,
  );
  const perWave = waveCounts(targets.length, spec.pcts);

  const jobs: CampaignJobEntry[] = [];
  const jobStore: FixtureCampaignJobs = {};
  let cursor = 0;
  const dispatch = (wave: number, count: number, fate: WaveFate) => {
    const slice = targets.slice(cursor, cursor + count);
    cursor += count;
    const open = fate.open ?? 0;
    const failed = fate.failed ?? 0;
    slice.forEach((deviceId, index) => {
      const jobId = `job:dl:${spec.tag}:${deviceId.replace('acs-device:', '')}`;
      const verdictless = index >= slice.length - open;
      const refused = !verdictless && index >= slice.length - open - failed;
      jobs.push({
        job_id: jobId,
        device_id: deviceId,
        kind: spec.dispatch_kind ?? 'rollout',
        wave,
      });
      jobStore[jobId] = verdictless
        ? { status: 'running' }
        : refused
          ? { status: 'failed' }
          : { status: 'completed', verdict: 'applied' };
    });
  };

  for (let wave = 0; wave < spec.current_wave; wave += 1) {
    dispatch(wave, perWave[wave] ?? 0, spec.waves[wave] ?? {});
  }
  const queuedCount = Math.min(spec.queued ?? 0, perWave[spec.current_wave] ?? 0);
  const dispatchedHere = (perWave[spec.current_wave] ?? 0) - queuedCount;
  dispatch(spec.current_wave, dispatchedHere, spec.waves[spec.current_wave] ?? {});

  return {
    doc: {
      campaign_id: spec.campaign_id,
      status: spec.status ?? 'running',
      file_id: spec.file_id,
      brand: row.brand,
      model: row.model,
      version: row.version,
      filter: spec.filter,
      waves: spec.pcts.map((pct) => ({ pct })),
      per_wave: perWave,
      current_wave: spec.current_wave,
      targets,
      queued: targets.slice(cursor, cursor + queuedCount),
      jobs,
      halt_on_fail_pct: spec.halt_on_fail_pct ?? 10,
      maintenance_window: spec.maintenance_window ?? null,
      dispatch_kind: spec.dispatch_kind ?? 'rollout',
      dispatch_file_id: spec.dispatch_file_id ?? spec.file_id,
      started_at: isoAgo(spec.started_ago),
      created_at: isoAgo(spec.started_ago),
      updated_at: isoAgo(Math.max(1, spec.started_ago - 20)),
    },
    jobs: jobStore,
  };
}

/**
 * The six verdicts, seeded against the fixture fleet.
 *
 * `started_ago` is also the list order: `GET /ops/v1/campaigns` answers newest first.
 */
export const FIXTURE_CAMPAIGNS: readonly FixtureCampaign[] = [
  // wait_window — created, wave 0 queued, and the window has not opened yet. The worker
  // reads the same window and holds; `:resume` is the door that may override it.
  // The interval is a fixed instant set on purpose: the cases that need `waiting`, `open` and
  // `passed` for this campaign inject a clock around it (`fixtureApi.test.ts:752`, `:862`,
  // `:1263`, `:1272`), so sliding it to «somewhere in the future» would break all three at once.
  // A reader on the wall clock after 2026-10-15T04:00Z correctly sees `passed`.
  buildCampaign({
    campaign_id: 'camp:ztek1',
    tag: 'zt',
    file_id: 'fw:2fd8a1c6b470',
    filter: { manufacturer: 'ZTE', model: 'F6600V9' },
    pcts: [10, 40, 100],
    current_wave: 0,
    waves: [{}],
    queued: 1,
    status: 'pending',
    maintenance_window: '2026-10-15T02:00:00Z/2026-10-15T04:00:00Z',
    started_ago: 35,
  }),
  // continue_wave — a rate-limited pass left part of wave 1 on the queue
  buildCampaign({
    campaign_id: 'camp:vsolk2',
    tag: 'vs',
    file_id: 'fw:b6540d3a9c21',
    filter: { manufacturer: 'VSOL', model: 'V1500GP-93128' },
    pcts: [30, 100],
    current_wave: 1,
    waves: [{}, {}],
    queued: 4,
    status: 'pending',
    started_ago: 80,
  }),
  // wait_jobs — wave 0 is dispatched, but the verify pass has not read the boards back
  buildCampaign({
    campaign_id: 'camp:fiberk3',
    tag: 'fh',
    file_id: 'fw:7e120c8f6b45',
    filter: { manufacturer: 'Fiberhome', model: 'AN5551-04-B' },
    pcts: [25, 100],
    current_wave: 0,
    waves: [{ open: 2 }],
    started_ago: 150,
  }),
  // idle — a rollback is one pass over the population; the ladder does not advance it
  buildCampaign({
    campaign_id: 'camp:rollback1',
    tag: 'rb',
    file_id: 'fw:4a7c19d2b0e3',
    filter: { manufacturer: 'Huawei', model: 'HS8546X6' },
    pcts: [100],
    current_wave: 0,
    waves: [{ failed: 1 }],
    status: 'rolling_back',
    dispatch_kind: 'rollback',
    dispatch_file_id: 'fw:91be07c4d5a2',
    started_ago: 240,
  }),
  // settled — the ladder reached 100%
  buildCampaign({
    campaign_id: 'camp:cdata4',
    tag: 'cd',
    file_id: 'fw:c30b59e7a18d',
    filter: { manufacturer: 'C-Data', model: 'GPT-741X' },
    pcts: [20, 100],
    current_wave: 1,
    waves: [{}, {}],
    status: 'completed',
    started_ago: 600,
  }),
  // settled — an auto-halt at the ceiling, so nothing dispatches until a human decides
  buildCampaign({
    campaign_id: 'camp:huawei5',
    tag: 'hw',
    file_id: 'fw:4a7c19d2b0e3',
    filter: { manufacturer: 'Huawei', model: 'HS8546X6' },
    pcts: [10, 50, 100],
    current_wave: 0,
    waves: [{ failed: 1 }],
    status: 'halted',
    halt_on_fail_pct: 5,
    started_ago: 900,
  }),
  // advance — wave 0 concluded cleanly, so the next rung is what the worker owes
  buildCampaign({
    campaign_id: 'camp:huawei6',
    tag: 'hw6',
    file_id: 'fw:91be07c4d5a2',
    filter: { manufacturer: 'Huawei', model: 'HS8546X6' },
    pcts: [20, 60, 100],
    current_wave: 0,
    waves: [{}],
    started_ago: 1200,
  }),
];

/** The `store.get_job` reader a campaign's derived numbers are read through. */
export function campaignJobReader(entry: FixtureCampaign): CampaignJobReader {
  return { getJob: (jobId: string) => entry.jobs[jobId] };
}

/**
 * A private copy of a seeded campaign.
 *
 * The provider needs one: `:resume` and `:rollback` move a record, and a fixture module
 * shared by every test in the file would otherwise carry the first test's ladder into
 * the next one.
 */
export function cloneCampaign(entry: FixtureCampaign): FixtureCampaign {
  return {
    doc: {
      ...entry.doc,
      filter: { ...entry.doc.filter },
      waves: entry.doc.waves.map((wave) => ({ ...wave })),
      per_wave: [...entry.doc.per_wave],
      targets: [...entry.doc.targets],
      queued: [...entry.doc.queued],
      jobs: entry.doc.jobs.map((job) => ({ ...job })),
    },
    jobs: { ...entry.jobs },
  };
}

/**
 * The fixture's dispatch pass — the observable half of `campaigns.py::dispatch_wave`.
 *
 * Every device this pass takes gets one download job whose board confirms it
 * (`completed` + `applied`), which is the only outcome a fixture may state: it has no NBI
 * to fail and no verify pass to disagree with. The failures an operator needs to *see* are
 * the seeded campaigns above, not numbers invented here. The wave then closes the way the
 * engine says it closes (`terminalFor`), and the auto-halt ceiling is judged with the same
 * `haltTriggered` the façade uses — so a wave that did fail cannot advance either.
 *
 * There is deliberately no token bucket on this path. The server's budget is what leaves a
 * wave `pending` with work `queued`; in fixture mode `:resume` dispatches the whole
 * remaining slice, and the cut-short shape comes from the seeded campaign instead.
 */
export function applyDispatch(
  entry: FixtureCampaign,
  opts: { wave: number; devices: readonly string[]; kind: string; now: Date },
): { dispatched: number; queued: string[]; status: string } {
  const doc = entry.doc;
  const tag = doc.campaign_id.replace(/^camp:/, '');
  for (const deviceId of opts.devices) {
    const jobId = `job:dl:${opts.kind === 'rollback' ? `${tag}-rb` : tag}:${deviceId.replace(
      'acs-device:',
      '',
    )}`;
    doc.jobs.push({ job_id: jobId, device_id: deviceId, kind: opts.kind, wave: opts.wave });
    entry.jobs[jobId] = { status: 'completed', verdict: 'applied' };
  }
  doc.current_wave = opts.wave;
  doc.queued = [];
  const { ok, failed } = waveJobCounts(campaignJobReader(entry), doc, opts.wave, opts.kind);
  doc.status = haltTriggered(doc, ok, failed) ? 'halted' : terminalFor(doc, opts.wave).status;
  doc.updated_at = opts.now.toISOString();
  return { dispatched: opts.devices.length, queued: [], status: doc.status };
}

/**
 * The id the registry would give this image, in the server's `fw:<12 hex>` form.
 *
 * The façade computes `fw:<sha256(brand|model|version)[:12]>`; the browser has no hash it
 * may present as an authority, so this is the same *shape* over the same triple — stable for
 * one `(brand, model, version)`, which is what the door's determinism is for, and not the
 * digest itself. The `file_id` a page shows for a fixture upload is fixture data.
 */
export function fixtureFileId(brand: string, model: string, version: string): string {
  return `fw:${digest(`${brand}|${model}|${version}`).slice(0, 12)}`;
}

/** The fixture fleet as the population scan sees it (`_compute_targets`' input rows). */
export function campaignPopulation(): CampaignPopulationDevice[] {
  return FIXTURE_DEVICES.map((device) => ({
    acs_device_id: device.acs_device_id,
    manufacturer: device.manufacturer,
    product_class: device.product_class,
    software_version: device.software_version,
  }));
}

/**
 * The running version of one device, in the server's order: the parameter snapshot's
 * `SoftwareVersion` leaf first, the device doc second (`campaigns.py:277-293`). The fixture
 * snapshot answers that leaf from the same record, so both reads agree.
 */
export function snapshotVersion(device: CampaignPopulationDevice): string | null {
  return device.software_version ?? null;
}
